import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  useTheme,
  Box,
  IconButton,
  Divider,
  Chip,
} from "@mui/material";
import FormSmallTextField from "../common/form/FormTextField";
import CustomTypography from "../common/form/CustomTypography";
import AppButton from "../common/buttons/AppButton";
import { FilterValue } from "../../../models/Filters/FilterValue";
import { Employee, Resetpassword } from "../../../models/Employees/Employee";
import { EmployeePermisson } from "../../../models/Employees/EmployeePermisson";
import { Team } from "../../../models/Employees/Team";
import CloseIcon from "@mui/icons-material/Close";
import AppSearchDropdownMulti from "../common/filter/AppSearchDropdownMulti";

interface EditEmployeePopupProps {
  open: boolean;
  employeeData?: Employee;
  employeePermissionList?: EmployeePermisson[];
  employeeTeamList?: Team[];
  canEditTeam?: boolean;
  isViewOnly?: boolean;
  isSuperAdminManager?: boolean;
  isAddMode?: boolean;
  discountPermission?: boolean;
  resetPw?: (email: Resetpassword | any) => void;
  onClose?: () => void;
  onSave?: (updatedEmployee: Employee) => void;
}

const EditEmployeePopup: React.FC<EditEmployeePopupProps> = ({
  open,
  employeeData,
  employeePermissionList,
  employeeTeamList,
  canEditTeam,
  isViewOnly,
  discountPermission,
  isAddMode,
  resetPw,
  isSuperAdminManager,
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const [employeeDetails, setEmployeeDetails] = useState<Employee>(
    employeeData || ({} as Employee),
  );

  useEffect(() => {
    if (open) {
      setEmployeeDetails(employeeData || ({} as Employee));
    }
  }, [open, employeeData]);

  const handleFieldChange = (
    fieldName: keyof Employee,
    value: string | number,
  ) => {
    setEmployeeDetails((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSelectPermissions = (selectedPermissions: FilterValue[]) => {
    const updatedPermissions = selectedPermissions.map((item) => ({
      role_id: Number(item.id),
      role: item.value,
    }));
    setEmployeeDetails((prev) => ({
      ...prev,
      permissions: updatedPermissions,
    }));
  };

  const handleSelectTeams = (selectedTeam: FilterValue[]) => {
    const updatedTeam = selectedTeam.map((item) => ({
      id: Number(item.id),
      name: item.value,
    }));
    setEmployeeDetails((prev) => ({
      ...prev,
      teams: updatedTeam,
    }));
  };

  const handleEdit = () => {
    if (!isViewOnly && onSave) {
      onSave(employeeDetails);
    }
  };

  const resetPassword = () => {
    if (resetPw) {
      resetPw(employeeDetails.email);
    }
  };

  const title = isAddMode
    ? "Add Employee"
    : isViewOnly
      ? "View Employee"
      : "Edit Employee";

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick" && onClose) onClose();
      }}
      fullWidth
      maxWidth="sm"
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            margin: { xs: "16px", sm: "24px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <CustomTypography variant="heading3">{title}</CustomTypography>
          <IconButton
            aria-label="close"
            onClick={onClose ?? (() => {})}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            maxHeight: "60vh",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors.greyTextColor,
              },
            },
          }}
        >
          <CustomTypography
            variant="heading6"
            style={{ fontWeight: 600, marginBottom: 16 }}
          >
            Basic Info
          </CustomTypography>
          <Box display="flex" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
            <Box sx={{ flex: "1 1 48%" }}>
              <FormSmallTextField
                sx={{ mb: 0 }}
                id="name"
                label="Name"
                fontSize="0.9rem"
                value={employeeDetails.name ?? ""}
                onChange={(_, value) => handleFieldChange("name", value ?? "")}
                disabled={isViewOnly}
              />
            </Box>
            <Box sx={{ flex: "1 1 48%" }}>
              <FormSmallTextField
                sx={{ mb: 0 }}
                id="email"
                label="Email"
                type="email"
                fontSize="0.9rem"
                value={employeeDetails.email ?? ""}
                onChange={(_, value) => handleFieldChange("email", value ?? "")}
                disabled={isViewOnly || !isAddMode}
              />
            </Box>
            <Box sx={{ flex: "1 1 48%" }}>
              <FormSmallTextField
                sx={{ mb: 0 }}
                id="discount_percentage"
                label="Discount"
                fontSize="0.9rem"
                value={
                  employeeDetails.discount_percentage
                    ? employeeDetails.discount_percentage.toString()
                    : ""
                }
                onChange={(_, value) =>
                  handleFieldChange("discount_percentage", value ?? "")
                }
                disabled={discountPermission}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />
          <CustomTypography
            variant="heading6"
            style={{ fontWeight: 600, marginBottom: 16 }}
          >
            Contact
          </CustomTypography>
          <Box display="flex" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
            <Box sx={{ flex: "0 1 120px" }}>
              <FormSmallTextField
                sx={{ mb: 0 }}
                id="calling_code"
                label="Calling Code"
                fontSize="0.9rem"
                value={employeeDetails.calling_code ?? ""}
                onChange={(_, value) =>
                  handleFieldChange("calling_code", value ?? "")
                }
                disabled={isViewOnly}
              />
            </Box>
            <Box sx={{ flex: "1 1 200px" }}>
              <FormSmallTextField
                sx={{ mb: 0 }}
                id="phone_number"
                label="Phone Number"
                fontSize="0.9rem"
                value={employeeDetails.phone_number ?? ""}
                onChange={(_, value) =>
                  handleFieldChange("phone_number", value ?? "")
                }
                disabled={isViewOnly}
              />
            </Box>
            {!isAddMode && resetPw && (
              <Box sx={{ alignSelf: "flex-end" }}>
                <AppButton
                  variant="secondary"
                  onClick={resetPassword}
                  label="Reset Password"
                />
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />
          <CustomTypography
            variant="heading6"
            style={{ fontWeight: 600, marginBottom: 16 }}
          >
            Permissions{" "}
            {employeePermissionList && employeePermissionList.length > 0
              ? `(${employeePermissionList.length.toString()})`
              : ""}
          </CustomTypography>
          <Box sx={{ mb: 3 }}>
            <AppSearchDropdownMulti
              borderColor={theme.palette.customColors.blackBorder}
              isMultiSelect={true}
              title="Select Permissions"
              fontSize="0.8rem"
              value={employeeDetails.permissions?.map((item) => ({
                id: String(item.role_id),
                value: String(item.role),
              }))}
              list={
                employeePermissionList?.map((item) => ({
                  id: String(item.role_id),
                  value: String(item.role),
                })) ?? []
              }
              local={true}
              handleSelect={handleSelectPermissions}
              disabled={isViewOnly}
            />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
              {employeeDetails.permissions?.map((item) => (
                <Chip
                  key={`perm-${item.role_id}-${item.role}`}
                  size="small"
                  label={item.role}
                  onDelete={
                    isViewOnly || isSuperAdminManager === true
                      ? undefined
                      : () => {
                          handleSelectPermissions(
                            (employeeDetails.permissions ?? [])
                              .filter(
                                (p) =>
                                  !(
                                    p.role_id === item.role_id &&
                                    p.role === item.role
                                  ),
                              )
                              .map((p) => ({
                                id: String(p.role_id),
                                value: String(p.role),
                              })),
                          );
                        }
                  }
                  sx={{
                    fontSize: "0.8rem",
                    height: "24px",
                  }}
                />
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />
          <CustomTypography
            variant="heading6"
            style={{ fontWeight: 600, marginBottom: 16 }}
          >
            Teams
          </CustomTypography>
          <Box sx={{ mb: 2 }}>
            <AppSearchDropdownMulti
              borderColor={theme.palette.customColors.blackBorder}
              isMultiSelect={true}
              title="Select Teams"
              fontSize="0.8rem"
              value={employeeDetails.teams?.map((item) => ({
                id: String(item.id),
                value: String(item.name),
              }))}
              list={
                employeeTeamList?.map((item) => ({
                  id: String(item.id),
                  value: String(item.name),
                })) ?? []
              }
              local={true}
              handleSelect={handleSelectTeams}
              disabled={isViewOnly || (!isSuperAdminManager && !canEditTeam)}
            />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
              {employeeDetails.teams?.map((item) => (
                <Chip
                  key={`team-${item.id}-${item.name}`}
                  size="small"
                  label={item.name}
                  onDelete={
                    isViewOnly || (!isSuperAdminManager && !canEditTeam)
                      ? undefined
                      : () => {
                          handleSelectTeams(
                            (employeeDetails.teams ?? [])
                              .filter(
                                (t) =>
                                  !(t.id === item.id && t.name === item.name),
                              )
                              .map((t) => ({
                                id: String(t.id),
                                value: String(t.name),
                              })),
                          );
                        }
                  }
                  sx={{
                    fontSize: "0.8rem",
                    height: "24px",
                  }}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          <Box />
          <Box sx={{ display: "flex", gap: 2 }}>
            <AppButton
              variant="grayscale"
              onClick={onClose ?? (() => {})}
              label="Cancel"
            />
            {!isViewOnly && (
              <AppButton variant="primary" onClick={handleEdit} label="Save" />
            )}
          </Box>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default EditEmployeePopup;
